import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function CourseFinalTestTab({ courseId, questions }) {
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [questionsPerAttempt, setQuestionsPerAttempt] = useState(5);
  const [passingScore, setPassingScore] = useState(70);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/courses/${courseId}/final-test`);
      setTest(data);
      if (data) {
        setSelected(new Set(data.question_ids));
        setQuestionsPerAttempt(data.questions_per_attempt);
        setPassingScore(data.passing_score);
      } else {
        setSelected(new Set());
        setQuestionsPerAttempt(Math.min(5, questions.length));
        setPassingScore(70);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [courseId, questions.length]);

  useEffect(() => { load(); }, [load]);

  const toggle = (qid) => {
    const next = new Set(selected);
    if (next.has(qid)) next.delete(qid);
    else next.add(qid);
    setSelected(next);
  };

  const save = async () => {
    if (selected.size === 0) {
      toast.error("Seleziona almeno una domanda");
      return;
    }
    const n = Number(questionsPerAttempt);
    if (!n || n < 1) {
      toast.error("Il numero domande per tentativo deve essere ≥ 1");
      return;
    }
    if (n > selected.size) {
      toast.error("Domande per tentativo non può superare il numero selezionate");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/courses/${courseId}/final-test`, {
        question_ids: Array.from(selected),
        questions_per_attempt: n,
        passing_score: Number(passingScore) || 70,
      });
      setTest(data);
      toast.success("Test finale salvato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere il test finale del corso?")) return;
    try {
      await api.delete(`/courses/${courseId}/final-test`);
      setTest(null);
      setSelected(new Set());
      toast.success("Test finale rimosso");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="text-sm text-brand-muted"><Loader2 size={16} className="inline animate-spin mr-2" /> Caricamento…</div>;
  }

  return (
    <div data-testid="final-test-tab" className="space-y-5">
      <div>
        <h2 className="text-xl text-brand-blue">Test finale del corso</h2>
        <p className="text-sm text-brand-muted">
          Configura il pool di domande da cui estrarre casualmente ad ogni tentativo. Tentativi illimitati per lo studente.
        </p>
      </div>

      {questions.length === 0 ? (
        <div className="bg-white border border-dashed border-brand-line rounded-sm p-8 text-center">
          <AlertTriangle size={24} className="mx-auto text-amber-500 mb-2" />
          <div className="text-brand-ink font-semibold">Nessuna domanda nel pool</div>
          <p className="text-sm text-brand-muted">Crea almeno una domanda prima di configurare il test finale.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-brand-line rounded-sm p-5 grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Domande estratte per tentativo</Label>
              <Input
                data-testid="ft-questions-per-attempt"
                type="number"
                min={1}
                value={questionsPerAttempt}
                onChange={(e) => setQuestionsPerAttempt(e.target.value)}
                className="mt-1.5 h-10 rounded-sm"
              />
              <div className="text-xs text-brand-muted mt-1">
                Selezionate: {selected.size}
              </div>
            </div>
            <div>
              <Label>Punteggio minimo per superare</Label>
              <Input
                data-testid="ft-passing-score"
                type="number"
                min={1}
                max={100}
                value={passingScore}
                onChange={(e) => setPassingScore(e.target.value)}
                className="mt-1.5 h-10 rounded-sm"
              />
            </div>
            <div className="flex items-end justify-end gap-2">
              {test && (
                <button
                  type="button"
                  onClick={remove}
                  data-testid="ft-delete-btn"
                  className="text-sm text-red-600 hover:underline underline-offset-4 inline-flex items-center gap-1.5"
                >
                  <Trash2 size={14} /> Rimuovi
                </button>
              )}
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="ft-save-btn"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salva test finale
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Seleziona il pool di domande</Label>
            {questions.map((q, i) => {
              const isSelected = selected.has(q.id);
              return (
                <label
                  key={q.id}
                  data-testid={`ft-question-${q.id}`}
                  className={
                    "flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors " +
                    (isSelected ? "border-brand-blue bg-brand-blue/5" : "border-brand-line hover:border-brand-blue/40 bg-white")
                  }
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(q.id)}
                    className="mt-1 accent-brand-blue"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-widest text-brand-muted">Domanda {i + 1}</div>
                    <div className="text-sm text-brand-ink">{q.text}</div>
                  </div>
                  {isSelected && <CheckCircle2 size={16} className="text-brand-blue shrink-0 mt-0.5" />}
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
